import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Truck, Package, Construction, Trash2, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';

const iconMap = {
  Truck: Truck,
  Package: Package,
  Construction: Construction,
  Trash2: Trash2
};

const Services = () => {
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="relative py-20 bg-zinc-900 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1629807472592-2649bfa09f9c"
            alt="Services"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/50 mb-4">Our Services</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Professional Tipper Truck Services
            </h1>
            <p className="text-xl text-gray-300">
              Comprehensive material delivery solutions for Adelaide Hills, Murray Bridge and surrounding areas
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-zinc-800/50 border-zinc-700 hover:border-orange-500 transition-all duration-300 group backdrop-blur-sm overflow-hidden">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-orange-500/30">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-white font-bold text-2xl mb-3 group-hover:text-orange-500 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 mb-6 leading-relaxed">{service.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold text-sm mb-3">Key Features:</h4>
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-zinc-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Additional Trucks Available
                  </h2>
                  <p className="text-gray-400 text-lg">
                    Need specialized equipment? We have additional trucks available upon request to meet your specific project requirements.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-orange-500 text-4xl font-bold mb-2">✓</div>
                    <div className="text-white font-semibold mb-1">Steel Body Tandem Tippers</div>
                    <div className="text-gray-400 text-sm">Ready for hire</div>
                  </div>
                  <div className="text-center">
                    <div className="text-orange-500 text-4xl font-bold mb-2">+</div>
                    <div className="text-white font-semibold mb-1">Additional Fleet</div>
                    <div className="text-gray-400 text-sm">Upon request</div>
                  </div>
                  <div className="text-center">
                    <div className="text-orange-500 text-4xl font-bold mb-2">100%</div>
                    <div className="text-white font-semibold mb-1">Fully Insured</div>
                    <div className="text-gray-400 text-sm">Safe & reliable</div>
                  </div>
                </div>
                <div className="text-center">
                  <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white px-8">
                    <Link to="/contact">
                      Request a Quote
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Service Coverage Areas
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              We proudly serve Adelaide Hills, Murray Bridge and all surrounding areas with professional tipper hire services
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Adelaide Hills', 'Murray Bridge', 'Mount Barker', 'Stirling', 'Hahndorf', 'Woodside', 'Gumeracha', 'Lobethal'].map((area, idx) => (
                <Badge key={idx} variant="outline" className="border-orange-500 text-orange-500 px-4 py-2 text-base">
                  {area}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
